// Admin observability dashboard (ADR-014). A green/yellow/red health board for faculty/staff
// so system health is visible without GCP access. Reads GET /api/admin/status (staff-only).
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { useCan } from "../../components/Can.js";
import { Badge, Card, CardBody, EmptyState, Spinner } from "../../components/ui.js";
// Dot / relativeAge / HEALTH_* moved to ./health.tsx so the COI panel (WO A3) shares them.
import { Dot, HEALTH_LABEL, HEALTH_TONE, relativeAge, type Health } from "./health.js";
import { CoiPanel } from "./CoiPanel.js";
import { DataOpsPanel } from "./DataOpsPanel.js";
import { SessionWatchPanel } from "./SessionWatchPanel.js";

interface PipelineStatus {
  pipeline: string;
  label: string;
  health: Health;
  status: string;
  lastRunAt: string | null;
  lastSuccessAt: string | null;
  freshnessSloMins: number;
  detail: Record<string, unknown> | null;
}

interface StatusResponse {
  overall: Health;
  checkedAt: string;
  database: { ok: boolean; health: Health };
  pipelines: PipelineStatus[];
}

// Compact Pipelines panel: a single collapsed summary line (worst health + counts) that
// expands to the per-pipeline detail. These cards track ops heartbeat freshness
// (pipeline_heartbeats vs SLO) — NOT the Airtable/editorial data seed. All-red with
// "never" simply means no job has POSTed /api/admin/heartbeat yet.
function PipelinesPanel({ pipelines }: { pipelines: PipelineStatus[] }): JSX.Element {
  const counts = { red: 0, yellow: 0, green: 0 };
  let newestSuccess: string | null = null;
  for (const p of pipelines) {
    if (p.health === "red" || p.health === "yellow" || p.health === "green") counts[p.health] += 1;
    if (p.lastSuccessAt && (!newestSuccess || p.lastSuccessAt > newestSuccess)) {
      newestSuccess = p.lastSuccessAt;
    }
  }
  const worst: Health = counts.red > 0 ? "red" : counts.yellow > 0 ? "yellow" : "green";
  const summaryCounts = [
    counts.red > 0 ? `${counts.red} red` : null,
    counts.yellow > 0 ? `${counts.yellow} yellow` : null,
    counts.green > 0 ? `${counts.green} green` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Card>
      <CardBody>
        <details className="group">
          <summary className="flex cursor-pointer flex-wrap items-center gap-2 text-sm [&::-webkit-details-marker]:hidden">
            {/* flex on <summary> suppresses the native disclosure triangle — draw our own */}
            <svg
              viewBox="0 0 16 16"
              className="h-3 w-3 shrink-0 text-slate-400 transition-transform group-open:rotate-90"
              aria-hidden
            >
              <path d="M6 3.5 11 8l-5 4.5V3.5z" fill="currentColor" />
            </svg>
            <Dot health={worst} />
            <span className="font-semibold">Pipelines</span>
            <span className="text-xs text-slate-500">{summaryCounts}</span>
            <span className="text-xs text-slate-400">
              {newestSuccess ? `newest success ${relativeAge(newestSuccess)}` : "no successes recorded"}
            </span>
          </summary>
          <div className="mt-2 space-y-2">
            <p className="text-xs text-slate-500">
              Freshness measured against SLO targets. Yellow = past target; red = well past target, errored, or never
              run. Red/unknown here means no heartbeat recorded yet — not a missing Airtable seed; editorial data may
              still be fine (see docs/ops/observability-admin.md).
            </p>
            {pipelines.map((p) => (
              <div
                key={p.pipeline}
                className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Dot health={p.health} />
                    <span className="font-medium">{p.label}</span>
                    <Badge tone={HEALTH_TONE[p.health]}>{p.status}</Badge>
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">
                    last success {relativeAge(p.lastSuccessAt)} · last run {relativeAge(p.lastRunAt)} · target ≤{" "}
                    {p.freshnessSloMins >= 60 ? `${Math.round(p.freshnessSloMins / 60)}h` : `${p.freshnessSloMins}m`}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </details>
      </CardBody>
    </Card>
  );
}

export function AdminDashboard(): JSX.Element {
  const can = useCan();
  const allowed = can("viewAuditTrail");
  const { data, isLoading, isError } = useQuery({
    queryKey: qk.adminStatus,
    queryFn: () => api.get<StatusResponse>("/admin/status"),
    enabled: allowed,
    refetchInterval: 30000,
  });

  if (!allowed) {
    return <EmptyState>The system status board is available to staff.</EmptyState>;
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">System Status</h1>
          <p className="text-sm text-slate-500">
            Pipeline health for faculty &amp; staff — no cloud console needed (ADR-014).
          </p>
        </div>
        {data && (
          <div className="flex items-center gap-2 text-sm">
            <Dot health={data.overall} />
            <span className="font-medium">{HEALTH_LABEL[data.overall]}</span>
            <span className="text-xs text-slate-400">checked {relativeAge(data.checkedAt)}</span>
          </div>
        )}
      </header>

      {isLoading ? (
        <Spinner label="Loading status…" />
      ) : isError || !data ? (
        <EmptyState>Could not load system status.</EmptyState>
      ) : (
        <div className="space-y-2">
          <Card>
            <CardBody className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Dot health={data.database.health} />
                <span className="font-medium">Database</span>
              </div>
              <Badge tone={HEALTH_TONE[data.database.health]}>{data.database.ok ? "connected" : "unreachable"}</Badge>
            </CardBody>
          </Card>

          <PipelinesPanel pipelines={data.pipelines} />
        </div>
      )}

      {/* C1 / pilot session watch — login watermarks + ui.* events. */}
      <SessionWatchPanel />

      {/* COI backend observability (WO A3) — independent of pipeline status fetch. */}
      <CoiPanel />

      {/* Staging/dev Data Ops — gated by features.dataOps.enabled from /api/health. */}
      <DataOpsPanel />
    </div>
  );
}
